import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportCard from './components/ReportCard';
import DocumentLibrary from './components/DocumentLibrary';
import AnalyticsPreview from './components/AnalyticsPreview';
import NotificationPanel from './components/NotificationPanel';

const ClientPortalDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    // Simulate loading client data
    const loadClientData = async () => {
      try {
        setLoading(true);
        // In production, fetch client-specific data based on user role
        const mockClientData = {
          clientName: user?.user_metadata?.full_name || 'Client User',
          companyName: 'GrowGuardian Client',
          subscriptionLevel: 'Premium',
          lastLogin: new Date()?.toISOString(),
          recentReports: [
            {
              id: 1,
              title: 'Monthly Compliance Report - November 2024',
              type: 'Compliance',
              generatedDate: '2024-11-15',
              status: 'available',
              summary: 'Overall compliance rating: 94%. All EPA regulations met. 2 minor recommendations for improvement.',
              fileSize: '2.4 MB',
              format: 'PDF'
            },
            {
              id: 2,
              title: 'Treatment Effectiveness Analysis - Q4 2024',
              type: 'Treatment',
              generatedDate: '2024-11-10',
              status: 'available',
              summary: 'Average treatment effectiveness: 87%. Top performing product: Organic Neem Oil. Cost savings: $3,200.',
              fileSize: '3.1 MB',
              format: 'PDF'
            },
            {
              id: 3,
              title: 'Performance Analytics Dashboard - October 2024',
              type: 'Performance',
              generatedDate: '2024-11-05',
              status: 'available',
              summary: 'Task completion rate: 96%. Team efficiency up 12%. Field coverage increased by 8 acres.',
              fileSize: '1.8 MB',
              format: 'PDF'
            }
          ],
          pendingDeliverables: [
            {
              id: 1,
              title: 'Annual Compliance Summary 2024',
              expectedDate: '2024-12-01',
              type: 'Compliance'
            },
            {
              id: 2,
              title: 'Year-End Treatment ROI Analysis',
              expectedDate: '2024-12-15',
              type: 'Treatment'
            }
          ],
          notifications: [
            {
              id: 1,
              message: 'New compliance report available for download',
              timestamp: '2024-11-15T10:30:00Z',
              type: 'report',
              read: false
            },
            {
              id: 2,
              message: 'Treatment analytics report ready for review',
              timestamp: '2024-11-10T14:20:00Z',
              type: 'report',
              read: false
            }
          ]
        };
        setClientData(mockClientData);
      } catch (error) {
        console.error('Error loading client data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClientData();
  }, [user]);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'compliance', label: 'Compliance Reports', icon: 'FileCheck' },
    { id: 'treatment', label: 'Treatment Reports', icon: 'Droplets' },
    { id: 'performance', label: 'Performance Analytics', icon: 'TrendingUp' },
    { id: 'documents', label: 'Document Library', icon: 'FolderOpen' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' }
  ];

  const dashboardMetrics = [
    {
      id: 1,
      title: 'Available Reports',
      value: clientData?.recentReports?.length || 0,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 2,
      title: 'Pending Deliverables',
      value: clientData?.pendingDeliverables?.length || 0,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      id: 3,
      title: 'Unread Notifications',
      value: clientData?.notifications?.filter(n => !n?.read)?.length || 0,
      icon: 'Bell',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 4,
      title: 'Subscription Level',
      value: clientData?.subscriptionLevel || 'N/A',
      icon: 'Award',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  const renderDashboardContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            {/* Metrics Strip */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dashboardMetrics?.map(metric => (
                <div key={metric?.id} className="bg-card rounded-lg border border-border p-4 agricultural-shadow">
                  <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-lg ${metric?.bgColor}`}>
                      <Icon name={metric?.icon} size={24} className={metric?.color} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">{metric?.title}</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{metric?.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Reports */}
            <div className="bg-card rounded-lg border border-border agricultural-shadow">
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Recent Reports</h3>
                <p className="text-sm text-muted-foreground">Your latest compliance, treatment, and performance reports</p>
              </div>
              <div className="p-6 space-y-4">
                {clientData?.recentReports?.map(report => (
                  <ReportCard key={report?.id} report={report} />
                ))}
              </div>
            </div>

            {/* Pending Deliverables */}
            <div className="bg-card rounded-lg border border-border agricultural-shadow">
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Pending Deliverables</h3>
                <p className="text-sm text-muted-foreground">Upcoming reports scheduled for delivery</p>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {clientData?.pendingDeliverables?.map(deliverable => (
                    <div key={deliverable?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="Clock" size={20} className="text-warning" />
                        <div>
                          <p className="font-medium text-foreground">{deliverable?.title}</p>
                          <p className="text-sm text-muted-foreground">{deliverable?.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-foreground">Expected</p>
                        <p className="text-sm text-muted-foreground">{new Date(deliverable?.expectedDate)?.toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Analytics Preview */}
            <AnalyticsPreview />
          </div>
        );
      case 'compliance': case'treatment': case'performance':
        return <DocumentLibrary reportType={activeSection} reports={clientData?.recentReports} />;
      case 'documents':
        return <DocumentLibrary reportType="all" reports={clientData?.recentReports} />;
      case 'notifications':
        return <NotificationPanel notifications={clientData?.notifications} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading client portal...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 bg-card border-r border-border min-h-screen">
          <div className="p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Building2" size={24} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{clientData?.companyName}</p>
                <p className="text-xs text-muted-foreground">{clientData?.subscriptionLevel} Plan</p>
              </div>
            </div>
          </div>
          
          <nav className="p-4 space-y-2">
            {sidebarItems?.map(item => (
              <button
                key={item?.id}
                onClick={() => setActiveSection(item?.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === item?.id
                    ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-medium">{item?.label}</span>
              </button>
            ))}
          </nav>

          {/* Quick Actions */}
          <div className="p-4 border-t border-border mt-auto">
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Quick Actions</p>
            <div className="space-y-2">
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left" className="w-full justify-start">
                Download All
              </Button>
              <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" className="w-full justify-start">
                Share Reports
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar Toggle */}
        <div className="lg:hidden fixed bottom-4 right-4 z-50">
          <Button variant="default" size="lg" iconName="Menu" className="rounded-full shadow-lg" />
        </div>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Client Portal</h1>
              <p className="text-muted-foreground mt-1">
                Welcome back, {clientData?.clientName}. Last login: {new Date(clientData?.lastLogin)?.toLocaleString()}
              </p>
            </div>

            {/* Content Area */}
            {renderDashboardContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientPortalDashboard;